import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';

// Layout
import Layout from './components/Layout';

// Pages
import LandingPage from './pages/LandingPage';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import Licenses from './pages/Licenses';
import LicenseDetail from './pages/LicenseDetail';
import CreateLicense from './pages/CreateLicense';
import Users from './pages/Users';
import ActivityLogs from './pages/ActivityLogs';
import SupportMessages from './pages/SupportMessages';
import Releases from './pages/Releases';
import APIKeys from './pages/APIKeys';
import AICredits from './pages/AICredits';
import BlogPosts from './pages/BlogPosts';
import Terms from './pages/Terms';
import Tutorials from './pages/Tutorials';
import BuyCredits from './pages/BuyCredits';

// Protected Route wrapper
const ProtectedRoute = ({ children, requiredRole }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && !['super_admin', 'admin'].includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/buy-credits" element={<BuyCredits />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="customers" element={<Customers />} />
        <Route path="customers/:id" element={<CustomerDetail />} />
        <Route path="licenses" element={<Licenses />} />
        <Route path="licenses/create" element={<CreateLicense />} />
        <Route path="licenses/:id" element={<LicenseDetail />} />
        <Route
          path="users"
          element={
            <ProtectedRoute requiredRole="admin">
              <Users />
            </ProtectedRoute>
          }
        />
        <Route path="activity" element={<ActivityLogs />} />
        <Route path="support" element={<SupportMessages />} />
        <Route
          path="releases"
          element={
            <ProtectedRoute requiredRole="admin">
              <Releases />
            </ProtectedRoute>
          }
        />
        <Route
          path="api-keys"
          element={
            <ProtectedRoute requiredRole="admin">
              <APIKeys />
            </ProtectedRoute>
          }
        />
        <Route
          path="ai-credits"
          element={
            <ProtectedRoute requiredRole="admin">
              <AICredits />
            </ProtectedRoute>
          }
        />
        <Route
          path="blog-posts"
          element={
            <ProtectedRoute requiredRole="admin">
              <BlogPosts />
            </ProtectedRoute>
          }
        />
        <Route
          path="tutorials"
          element={
            <ProtectedRoute requiredRole="admin">
              <Tutorials />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
